// Service Steady PWA — typed fetch wrapper for the Worker /pwa/* API.
// v2.0 — built 2026-05-02 — Chunk 2 deliverable.
//
// REPLACES the Chunk-1 stub. All endpoints credentialed so the Cloudflare
// Access JWT cookie rides along; the Worker validates Cf-Access-Jwt-Assertion.
// Errors map to two thrown classes:
//   - ApiAuthError  — 401 / 403 -> AuthGate redirects to login
//   - ApiError      — anything else (carries status + body for surface text)
//
// Types live in @/types/api (mirror of Worker src/types.ts contract block).
//
// Function names kept stable for downstream consumers being built in parallel:
//   fetchTenant, fetchConversations / listConversations, fetchConversationDetail
//   / fetchConversation, sendConversationReply / postReply, triggerReviewRequest
//   / postReviewRequest, fetchToday, fetchReports.

import type {
  AuditLogPage,
  ConversationDetail,
  ConversationsListFilter,
  ConversationsListPage,
  DailyRecapSettings,
  OwnerProfile,
  PwaMessage,
  QuickReplyTemplate,
  QuickReplyTemplatesPayload,
  Reminder,
  ReportsPeriod,
  ReportsSnapshot,
  ReviewRequestResult,
  TenantConfig,
  TodaySnapshot,
} from '@/types/api';

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.servicesteady.com';

export class ApiAuthError extends Error {
  constructor(message = 'Not authenticated') {
    super(message);
    this.name = 'ApiAuthError';
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// 2026-06-29 (NotificationClick502) — transient-error retry policy for React
// Query. A GHL rate-limit (429) or gateway blip (502/503/504) on the
// conversation-detail fetch used to dead-end on a hard error that in-place
// refetch (the old global `retry: 1`) just re-hit — only a force-close
// recovered. Retry transient statuses + network errors with backoff; NEVER
// retry a real 4xx (404 etc.) or an auth error (Cloudflare Access handles auth —
// a retry can't conjure a missing JWT). Wired as the global QueryClient default
// in main.tsx, so every PWA query self-recovers from a GHL throttle.
const TRANSIENT_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

export function isTransientApiError(error: unknown): boolean {
  if (error instanceof ApiAuthError) return false;
  if (error instanceof ApiError) return TRANSIENT_STATUSES.has(error.status);
  // A fetch network failure / abort surfaces as TypeError — worth a retry.
  return error instanceof TypeError;
}

/** React Query `retry` predicate: retry transient errors up to 3× (4 attempts total). */
export function retryTransient(failureCount: number, error: unknown): boolean {
  return isTransientApiError(error) && failureCount < 3;
}

/** React Query `retryDelay`: 1s → 2s → 4s, capped at 8s, + jitter — gives a
 *  throttled GHL time to recover between attempts. */
export function retryDelayBackoff(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 8000) + Math.random() * 250;
}

interface RequestOptions extends RequestInit {
  /** Skip JSON parse — used by callers that want the raw Response. */
  raw?: boolean;
}

async function request<T>(path: string, init?: RequestOptions): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 401 || res.status === 403) {
    throw new ApiAuthError();
  }
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      try {
        body = await res.text();
      } catch {
        body = null;
      }
    }
    throw new ApiError(res.status, `API ${res.status} ${res.statusText}`, body);
  }
  if (init?.raw) return res as unknown as T;
  return (await res.json()) as T;
}

// Build a query string from a sparse object — drops undefined/null/empty.
function qs(params: Record<string, string | number | undefined | null>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

// ---------------------------------------------------------------------------
// 1. GET /pwa/tenant/:slug -> TenantConfig
// ---------------------------------------------------------------------------

export async function fetchTenant(slug: string): Promise<TenantConfig> {
  if (!slug) throw new Error('slug required');
  return request<TenantConfig>(`/pwa/tenant/${encodeURIComponent(slug)}`);
}

// ---------------------------------------------------------------------------
// 2. GET /pwa/conversations -> ConversationsListPage
// ---------------------------------------------------------------------------

export interface FetchConversationsArgs {
  slug: string;
  filter?: ConversationsListFilter;
  search?: string;
  limit?: number;
  offset?: number;
}

export async function fetchConversations(
  args: FetchConversationsArgs,
): Promise<ConversationsListPage> {
  return request<ConversationsListPage>(
    `/pwa/conversations${qs({
      slug: args.slug,
      filter: args.filter ?? 'all',
      search: args.search ?? '',
      limit: args.limit ?? 50,
      offset: args.offset ?? 0,
    })}`,
  );
}

/** Back-compat alias for the Chunk-1 caller name. */
export const listConversations = (args: FetchConversationsArgs) =>
  fetchConversations(args);

// ---------------------------------------------------------------------------
// 3. GET /pwa/conversations/:id -> ConversationDetail
// ---------------------------------------------------------------------------

export async function fetchConversationDetail(
  conversationId: string,
  slug: string,
): Promise<ConversationDetail> {
  if (!conversationId) throw new Error('conversationId required');
  if (!slug) throw new Error('slug required');
  return request<ConversationDetail>(
    `/pwa/conversations/${encodeURIComponent(conversationId)}${qs({ slug })}`,
  );
}

/** Back-compat alias for the Chunk-1 caller name. */
export const fetchConversation = (id: string, slug: string) =>
  fetchConversationDetail(id, slug);

// ---------------------------------------------------------------------------
// 4. POST /pwa/conversations/:id/reply -> PwaMessage (echo)
// ---------------------------------------------------------------------------

export async function sendConversationReply(args: {
  conversationId: string;
  slug: string;
  body: string;
}): Promise<PwaMessage> {
  return request<PwaMessage>(
    `/pwa/conversations/${encodeURIComponent(args.conversationId)}/reply`,
    {
      method: 'POST',
      body: JSON.stringify({ slug: args.slug, body: args.body }),
    },
  );
}

/** Back-compat alias for the Chunk-1 caller name. */
export const postReply = (id: string, slug: string, body: string) =>
  sendConversationReply({ conversationId: id, slug, body });

// ---------------------------------------------------------------------------
// 4b. POST /pwa/conversations/:id/resolve -> MarkResolvedResult
// ---------------------------------------------------------------------------
//
// Wires the Chunk-5 "Mark resolved" comms action. Worker handler adds the
// `pwa_resolved` tag to the contact behind the conversation. The route is
// live in the deployed Worker, so a failure here is a real failure and must
// surface to the owner — we never fake success for a write that the UI
// celebrates as done.

export interface MarkResolvedResult {
  resolved: boolean;
  conversation_id: string;
  contact_id: string;
  tag: string;
}

/**
 * POST /pwa/conversations/:id/mark-booked — owner confirms the contact
 * booked. Adds `pwa_booked` and removes `pwa_resolved` server-side so
 * the thread flips to `booked` status in deriveStatus.
 */
export async function markBookedConversation(args: {
  conversationId: string;
  slug: string;
}): Promise<{ booked: true }> {
  if (!args.conversationId) throw new Error('conversationId required');
  if (!args.slug) throw new Error('slug required');
  return request<{ booked: true }>(
    `/pwa/conversations/${encodeURIComponent(args.conversationId)}/mark-booked`,
    {
      method: 'POST',
      body: JSON.stringify({ slug: args.slug }),
    },
  );
}

/**
 * POST /pwa/conversations/:id/unmark-booked — undo Mark Booked. Removes
 * the `pwa_booked` tag.
 */
export async function unmarkBookedConversation(args: {
  conversationId: string;
  slug: string;
}): Promise<{ booked: false }> {
  if (!args.conversationId) throw new Error('conversationId required');
  if (!args.slug) throw new Error('slug required');
  return request<{ booked: false }>(
    `/pwa/conversations/${encodeURIComponent(args.conversationId)}/unmark-booked`,
    {
      method: 'POST',
      body: JSON.stringify({ slug: args.slug }),
    },
  );
}

/**
 * POST /pwa/conversations/:id/unresolve — undo Archive. Removes the
 * `pwa_resolved` tag from the contact in GHL.
 */
export async function unarchiveConversation(args: {
  conversationId: string;
  slug: string;
}): Promise<{ resolved: false }> {
  if (!args.conversationId) throw new Error('conversationId required');
  if (!args.slug) throw new Error('slug required');
  return request<{ resolved: false }>(
    `/pwa/conversations/${encodeURIComponent(args.conversationId)}/unresolve`,
    {
      method: 'POST',
      body: JSON.stringify({ slug: args.slug }),
    },
  );
}

export interface TakeoverResult {
  paused: boolean;
  conversation_id?: string;
  contact_id?: string;
  tag?: string;
}

/**
 * POST /pwa/conversations/:id/takeover — owner is taking the wheel.
 * Tags the contact `shelby_paused` in GHL and writes a 30-day KV flag
 * so SMS Shelby skips AI generation on subsequent inbound on this
 * thread.
 */
export async function takeoverConversation(args: {
  conversationId: string;
  slug: string;
}): Promise<TakeoverResult> {
  if (!args.conversationId) throw new Error('conversationId required');
  if (!args.slug) throw new Error('slug required');
  return request<TakeoverResult>(
    `/pwa/conversations/${encodeURIComponent(args.conversationId)}/takeover`,
    {
      method: 'POST',
      body: JSON.stringify({ slug: args.slug }),
    },
  );
}

/**
 * POST /pwa/conversations/:id/stop-drip — owner manually halts the GHL
 * drip workflow on this contact WITHOUT pausing Shelby SMS replies. The
 * Worker writes `quote_followup_stop` + removes drip-trigger tags +
 * patches the cache. Used by the green drip pill on the conversation
 * header (2026-06-02).
 */
export interface StopDripResult {
  stopped: boolean;
  conversation_id: string;
  contact_id: string;
}

export async function stopDripConversation(args: {
  conversationId: string;
  slug: string;
}): Promise<StopDripResult> {
  if (!args.conversationId) throw new Error('conversationId required');
  if (!args.slug) throw new Error('slug required');
  return request<StopDripResult>(
    `/pwa/conversations/${encodeURIComponent(args.conversationId)}/stop-drip`,
    {
      method: 'POST',
      body: JSON.stringify({ slug: args.slug }),
    },
  );
}

// ---------------------------------------------------------------------------
// 4c. POST /pwa/conversations/:id/log-quote -> LogQuoteResult
// ---------------------------------------------------------------------------
//
// 2026-05-19 — Owner-side manual quote logging. When the owner quotes a
// customer off-Shelby (e.g. picked up the call themselves), this writes the
// same fields Shelby's analyzer would have written, plus optionally fires
// the recap SMS + drip enrollment by tagging the contact for the v2 recap
// workflow.

export interface LogQuoteResult {
  logged: boolean;
  conversation_id: string;
  contact_id: string;
  amount: number;
  service_label: string;
  vehicle: string | null;
  send_recap_requested: boolean;
  recap_triggered: boolean;
}

export interface LogQuoteRequest {
  conversationId: string;
  slug: string;
  amount: number;
  service_label: string;
  service_id?: string;
  vehicle?: string;
  send_recap: boolean;
}

export async function logManualQuote(args: LogQuoteRequest): Promise<LogQuoteResult> {
  if (!args.conversationId) throw new Error('conversationId required');
  if (!args.slug) throw new Error('slug required');
  if (!Number.isFinite(args.amount) || args.amount <= 0) {
    throw new Error('amount must be a positive number');
  }
  if (!args.service_label || !args.service_label.trim()) {
    throw new Error('service_label required');
  }
  return request<LogQuoteResult>(
    `/pwa/conversations/${encodeURIComponent(args.conversationId)}/log-quote`,
    {
      method: 'POST',
      body: JSON.stringify({
        slug: args.slug,
        amount: args.amount,
        service_label: args.service_label,
        service_id: args.service_id,
        vehicle: args.vehicle,
        send_recap: args.send_recap,
      }),
    },
  );
}

// ---------------------------------------------------------------------------
// 4d. POST /pwa/quotes/new -> CreateQuoteResult
// ---------------------------------------------------------------------------
//
// 2026-05-20 — Cold owner-handled call entry point. When the customer
// doesn't yet exist in the PWA Conversations list (cellular call to
// personal cell, walk-in, etc.), this finds-or-creates the GHL contact
// by phone, then writes the same fields + fires the same recap/drip
// path as logManualQuote.

export interface CreateQuoteResult {
  logged: boolean;
  contact_id: string;
  phone: string;
  first_name: string;
  last_name: string | null;
  amount: number;
  service_label: string;
  vehicle: string | null;
  send_recap_requested: boolean;
  recap_triggered: boolean;
}

export interface CreateQuoteRequest {
  slug: string;
  phone: string;
  first_name: string;
  last_name?: string;
  amount: number;
  service_label: string;
  service_id?: string;
  vehicle?: string;
  send_recap: boolean;
}

export async function createManualQuote(args: CreateQuoteRequest): Promise<CreateQuoteResult> {
  if (!args.slug) throw new Error('slug required');
  if (!args.phone || !args.phone.trim()) throw new Error('phone required');
  if (!args.first_name || !args.first_name.trim()) throw new Error('first_name required');
  if (!Number.isFinite(args.amount) || args.amount <= 0) {
    throw new Error('amount must be a positive number');
  }
  if (!args.service_label || !args.service_label.trim()) {
    throw new Error('service_label required');
  }
  return request<CreateQuoteResult>('/pwa/quotes/new', {
    method: 'POST',
    body: JSON.stringify({
      slug: args.slug,
      phone: args.phone,
      first_name: args.first_name,
      last_name: args.last_name,
      amount: args.amount,
      service_label: args.service_label,
      service_id: args.service_id,
      vehicle: args.vehicle,
      send_recap: args.send_recap,
    }),
  });
}

export async function markConversationResolved(args: {
  conversationId: string;
  slug: string;
}): Promise<MarkResolvedResult> {
  if (!args.conversationId) throw new Error('conversationId required');
  if (!args.slug) throw new Error('slug required');
  return request<MarkResolvedResult>(
    `/pwa/conversations/${encodeURIComponent(args.conversationId)}/resolve`,
    {
      method: 'POST',
      body: JSON.stringify({ slug: args.slug }),
    },
  );
}

// ---------------------------------------------------------------------------
// 4e. Server-side read state — POST /pwa/conversations/:id/mark-read
//     and bulk POST /pwa/conversations/mark-read
// ---------------------------------------------------------------------------
//
// Durable, cross-device read marks (replaces localStorage-only tracking).
// Best-effort: callers fire-and-forget — a failed mark-read just leaves the
// dot until the next open, never blocks the UI. The conversations list reads
// these marks back to compute `unread`.

export async function markConversationReadRemote(
  slug: string,
  conversationId: string,
  readAtMs?: number,
): Promise<void> {
  if (!slug || !conversationId) return;
  await request(`/pwa/conversations/${encodeURIComponent(conversationId)}/mark-read`, {
    method: 'POST',
    body: JSON.stringify({ slug, read_at: readAtMs }),
  });
}

export async function markConversationsReadBulkRemote(
  slug: string,
  conversationIds: string[],
  readAtMs?: number,
): Promise<void> {
  if (!slug || conversationIds.length === 0) return;
  await request('/pwa/conversations/mark-read', {
    method: 'POST',
    body: JSON.stringify({ slug, conversation_ids: conversationIds, read_at: readAtMs }),
  });
}

// ---------------------------------------------------------------------------
// 5. POST /pwa/contacts/:id/review-request -> ReviewRequestResult
// ---------------------------------------------------------------------------

export async function triggerReviewRequest(args: {
  contactId: string;
  slug: string;
}): Promise<ReviewRequestResult> {
  return request<ReviewRequestResult>(
    `/pwa/contacts/${encodeURIComponent(args.contactId)}/review-request`,
    {
      method: 'POST',
      body: JSON.stringify({ slug: args.slug }),
    },
  );
}

/** 2026-07-02 (DemoPolish 2) — review request to a hand-entered phone number.
 *  Worker find-or-creates the contact, then reuses the exact same send path
 *  (locked template + review URL). */
export async function triggerReviewRequestByPhone(args: {
  slug: string;
  phone: string;
  name?: string;
}): Promise<ReviewRequestResult> {
  return request<ReviewRequestResult>(`/pwa/review-request-by-phone`, {
    method: 'POST',
    body: JSON.stringify({ slug: args.slug, phone: args.phone, ...(args.name ? { name: args.name } : {}) }),
  });
}

/** Back-compat alias for the Chunk-1 caller name. */
export const postReviewRequest = (contactId: string, slug: string) =>
  triggerReviewRequest({ contactId, slug });

// ---------------------------------------------------------------------------
// 6. GET /pwa/today -> TodaySnapshot
// ---------------------------------------------------------------------------

export async function fetchToday(slug: string): Promise<TodaySnapshot> {
  if (!slug) throw new Error('slug required');
  return request<TodaySnapshot>(`/pwa/today${qs({ slug })}`);
}

// ---------------------------------------------------------------------------
// 7. GET /pwa/reports -> ReportsSnapshot
// ---------------------------------------------------------------------------

export async function fetchReports(args: {
  slug: string;
  period?: ReportsPeriod;
  /** Custom range (period='custom') — YYYY-MM-DD, tenant-local, inclusive. */
  from?: string;
  to?: string;
}): Promise<ReportsSnapshot> {
  if (!args.slug) throw new Error('slug required');
  return request<ReportsSnapshot>(
    `/pwa/reports${qs({
      slug: args.slug,
      period: args.period ?? 'week',
      ...(args.from && args.to ? { from: args.from, to: args.to } : {}),
    })}`,
  );
}

// ---------------------------------------------------------------------------
// 7b. Reminders (2026-07-02, Batch P4) — per-chat follow-up reminders
// ---------------------------------------------------------------------------

export async function listReminders(args: {
  slug: string;
  contactId?: string;
}): Promise<{ reminders: Reminder[] }> {
  if (!args.slug) throw new Error('slug required');
  return request<{ reminders: Reminder[] }>(
    `/pwa/reminders${qs({ slug: args.slug, ...(args.contactId ? { contact_id: args.contactId } : {}) })}`,
  );
}

export async function createReminder(args: {
  slug: string;
  contactId: string;
  conversationId: string;
  label: string;
  dueAtIso: string;
  note?: string;
}): Promise<{ reminder: Reminder }> {
  return request<{ reminder: Reminder }>(`/pwa/reminders`, {
    method: 'POST',
    body: JSON.stringify({
      slug: args.slug,
      contact_id: args.contactId,
      conversation_id: args.conversationId,
      label: args.label,
      due_at_iso: args.dueAtIso,
      note: args.note ?? '',
    }),
  });
}

export async function updateReminder(args: {
  id: string;
  dueAtIso?: string;
  note?: string;
}): Promise<{ reminder: Reminder }> {
  return request<{ reminder: Reminder }>(`/pwa/reminders/${encodeURIComponent(args.id)}`, {
    method: 'PUT',
    body: JSON.stringify({
      ...(args.dueAtIso !== undefined ? { due_at_iso: args.dueAtIso } : {}),
      ...(args.note !== undefined ? { note: args.note } : {}),
    }),
  });
}

export async function cancelReminder(args: { id: string }): Promise<{ ok: true; id: string }> {
  return request<{ ok: true; id: string }>(
    `/pwa/reminders/${encodeURIComponent(args.id)}/cancel`,
    { method: 'POST', body: '{}' },
  );
}

// ---------------------------------------------------------------------------
// 8. Web Push subscription endpoints
// ---------------------------------------------------------------------------
// Worker exposes:
//   POST /pwa/push/subscribe   { slug, subscription, ua? }   -> { id, ok }
//   POST /pwa/push/unsubscribe { slug, endpoint? | id? }     -> { ok }
//
// We send the full PushSubscription JSON because the Worker stores endpoint
// + p256dh + auth keys in KV/D1 keyed by slug for fan-out. A failure here
// means the owner won't receive customer notifications, so it must surface
// (the hook re-probes + records the error) rather than fake a subscription.

export interface PushSubscriptionPayload {
  endpoint: string;
  expirationTime: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface SubscribePushResult {
  id: string;
  ok: boolean;
}

export interface UnsubscribePushResult {
  ok: boolean;
}

function isWorkerRouteMissing(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 404 &&
    typeof err.body === 'object' &&
    err.body !== null &&
    'error' in err.body &&
    (err.body as { error: string }).error === 'pwa_route_not_found'
  );
}

export async function subscribePush(
  slug: string,
  subscription: PushSubscriptionPayload,
): Promise<SubscribePushResult> {
  if (!slug) throw new Error('slug required');
  if (!subscription || !subscription.endpoint) {
    throw new Error('subscription.endpoint required');
  }
  // Stable per-device id so re-subscribes overwrite the same KV row instead
  // of creating duplicates. Stored in localStorage so the same device keeps
  // the same subscription record across PWA reloads.
  let deviceId = '';
  try {
    deviceId = window.localStorage.getItem('ss_push_device_id') || '';
    if (!deviceId) {
      deviceId = `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem('ss_push_device_id', deviceId);
    }
  } catch {
    // Private mode — fall back to undefined; Worker will hash the endpoint.
  }
  // Worker reads slug from query param; subscription fields live at the
  // top level of the body (endpoint + keys + deviceId).
  return request<SubscribePushResult>(
    `/pwa/push/subscribe?slug=${encodeURIComponent(slug)}`,
    {
      method: 'POST',
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: subscription.keys,
        expirationTime: subscription.expirationTime,
        deviceId: deviceId || undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }),
    },
  );
}

export async function unsubscribePush(
  slug: string,
  endpointOrId: { endpoint?: string; id?: string },
): Promise<UnsubscribePushResult> {
  if (!slug) throw new Error('slug required');
  if (!endpointOrId.endpoint && !endpointOrId.id) {
    throw new Error('endpoint or id required');
  }
  // Same shape rule as subscribe — slug rides on the query string.
  return request<UnsubscribePushResult>(
    `/pwa/push/unsubscribe?slug=${encodeURIComponent(slug)}`,
    {
      method: 'POST',
      body: JSON.stringify({ ...endpointOrId }),
    },
  );
}

// ---------------------------------------------------------------------------
// 9. Premium polish v2 — owner profile, quick replies, audit, exports
// ---------------------------------------------------------------------------
//
// All endpoints are graceful-degrade safe: if the Worker hasn't been deployed
// with the new routes yet (404 pwa_route_not_found), the helper returns a
// stubbed safe value so the PWA still renders rather than throwing.

export async function fetchOwnerProfile(slug: string): Promise<OwnerProfile> {
  if (!slug) throw new Error('slug required');
  try {
    return await request<OwnerProfile>(
      `/pwa/tenant/${encodeURIComponent(slug)}/owner-profile`,
    );
  } catch (err) {
    if (isWorkerRouteMissing(err)) {
      return { owner_name: '', role: '', email_signature: '' };
    }
    throw err;
  }
}

// --- Notification prefs incl. Quiet hours (2026-07-09, server-persisted) ---

export interface ServerQuietHours {
  enabled: boolean;
  start: string;
  end: string;
}
export interface ServerNotifPrefs {
  followupNeeded: boolean;
  callbackRecap: boolean;
  quoteRecap: boolean;
  quietHours: ServerQuietHours;
  pausedUntil: number | null;
  updated_at?: string;
}
export interface NotifPrefsEnvelope {
  configured: boolean;
  prefs: ServerNotifPrefs | null;
}

export async function fetchNotifPrefs(slug: string): Promise<NotifPrefsEnvelope> {
  if (!slug) throw new Error('slug required');
  try {
    return await request<NotifPrefsEnvelope>(
      `/pwa/tenant/${encodeURIComponent(slug)}/notif-prefs`,
    );
  } catch (err) {
    if (isWorkerRouteMissing(err)) return { configured: false, prefs: null };
    throw err;
  }
}

export async function saveNotifPrefs(
  slug: string,
  prefs: ServerNotifPrefs,
): Promise<NotifPrefsEnvelope> {
  if (!slug) throw new Error('slug required');
  return request<NotifPrefsEnvelope>(
    `/pwa/tenant/${encodeURIComponent(slug)}/notif-prefs`,
    { method: 'PUT', body: JSON.stringify(prefs) },
  );
}

export async function saveOwnerProfile(
  slug: string,
  profile: OwnerProfile,
): Promise<OwnerProfile> {
  if (!slug) throw new Error('slug required');
  return request<OwnerProfile>(
    `/pwa/tenant/${encodeURIComponent(slug)}/owner-profile`,
    {
      method: 'PUT',
      body: JSON.stringify(profile),
    },
  );
}

// 2026-06-30 (DailyRecapPush) — evening recap push settings. Same
// graceful-degrade pattern: an old Worker (route missing) reads as the
// default OFF state so the card renders without erroring.
export async function fetchDailyRecapSettings(slug: string): Promise<DailyRecapSettings> {
  if (!slug) throw new Error('slug required');
  try {
    return await request<DailyRecapSettings>(
      `/pwa/tenant/${encodeURIComponent(slug)}/daily-recap`,
    );
  } catch (err) {
    if (isWorkerRouteMissing(err)) {
      return { enabled: false, time: '20:00', cadence: 'daily' };
    }
    throw err;
  }
}

export async function saveDailyRecapSettings(
  slug: string,
  settings: DailyRecapSettings,
): Promise<DailyRecapSettings> {
  if (!slug) throw new Error('slug required');
  return request<DailyRecapSettings>(
    `/pwa/tenant/${encodeURIComponent(slug)}/daily-recap`,
    {
      method: 'PUT',
      body: JSON.stringify(settings),
    },
  );
}

export async function fetchQuickReplies(slug: string): Promise<QuickReplyTemplate[]> {
  if (!slug) throw new Error('slug required');
  try {
    const payload = await request<QuickReplyTemplatesPayload>(
      `/pwa/tenant/${encodeURIComponent(slug)}/quick-replies`,
    );
    return payload.templates ?? [];
  } catch (err) {
    if (isWorkerRouteMissing(err)) {
      return [];
    }
    throw err;
  }
}

export async function saveQuickReplies(
  slug: string,
  templates: QuickReplyTemplate[],
): Promise<QuickReplyTemplate[]> {
  if (!slug) throw new Error('slug required');
  const payload = await request<QuickReplyTemplatesPayload>(
    `/pwa/tenant/${encodeURIComponent(slug)}/quick-replies`,
    {
      method: 'PUT',
      body: JSON.stringify({ templates } satisfies QuickReplyTemplatesPayload),
    },
  );
  return payload.templates ?? [];
}

export async function fetchAuditLog(args: {
  slug: string;
  limit?: number;
  cursor?: string | null;
}): Promise<AuditLogPage> {
  if (!args.slug) throw new Error('slug required');
  try {
    return await request<AuditLogPage>(
      `/pwa/audit/log${qs({
        slug: args.slug,
        limit: args.limit ?? 50,
        cursor: args.cursor ?? '',
      })}`,
    );
  } catch (err) {
    if (isWorkerRouteMissing(err)) {
      return { entries: [], next_cursor: null };
    }
    throw err;
  }
}

/** Builds the absolute URL for a download endpoint (data export, reports). */
export function buildExportUrl(path: string): string {
  return `${API_BASE}${path}`;
}

// ---------------------------------------------------------------------------
// Weather + location search — moved to ./api/weather.ts in v2 (2026-06-04).
// Consumers should import from '@/lib/api/weather' directly.
// ---------------------------------------------------------------------------
// Re-exports — keep the old `api.request` escape hatch and the contract
// types so consumers don't have to know about the file split.
// ---------------------------------------------------------------------------

export const api = { request };

export type {
  ActivityItem,
  AuditLogEntry,
  AuditLogPage,
  ConversationContact,
  ConversationDetail,
  ConversationLastMessage,
  ConversationStatus,
  ConversationSummary,
  ConversationTranscript,
  ConversationsListFilter,
  ConversationsListPage,
  DailyRecapSettings,
  MessageDirection,
  MessageSource,
  OwnerProfile,
  PwaMessage,
  QuickReplyTemplate,
  QuickReplyTemplatesPayload,
  Reminder,
  ReportsCallsPerDayPoint,
  ReportsDeltas,
  ReportsFunnel,
  ReportsPeriod,
  ReportsRange,
  ReportsSnapshot,
  ReportsMetricsV2,
  ReportsStages,
  ReviewRequestResult,
  TenantBrand,
  TenantConfig,
  TodaySnapshot,
} from '@/types/api';

// Back-compat type aliases for Chunk-1 names that may already be imported.
export type { PwaMessage as Message } from '@/types/api';
export type { ConversationsListFilter as ConversationFilter } from '@/types/api';
export type { ConversationsListPage as ConversationsPage } from '@/types/api';
export type { MessageSource as ConversationSource } from '@/types/api';
